// frontend/src/pages/InstallationRequestsPage.jsx

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { 
  Home, MapPin, Calendar, Loader2, Eye, DollarSign, 
  Search, RefreshCw, Clock, CheckCircle, XCircle, 
  Zap, Users, Building, Plus, Send, CreditCard, AlertCircle,
  TrendingUp, FileText, MessageCircle, ChevronDown, ChevronUp, Lock,
  Filter
} from 'lucide-react';
import { PageSEO } from '../components/SEO/PageSEO';
import config from '../config';

export const InstallationRequestsPage = () => {
  const { user } = useAuthStore();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [expandedRow, setExpandedRow] = useState(null);
  
  const [showBidModal, setShowBidModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [bidAmount, setBidAmount] = useState('');
  const [bidDuration, setBidDuration] = useState('');
  const [bidDescription, setBidDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [bidError, setBidError] = useState('');

  const pageSize = 20;
  const isInstaller = user?.role === 'installer';
  const isCustomer = user?.role === 'customer';
  
  useEffect(() => {
    fetchRequests();
  }, [currentPage, statusFilter]);

  // ✅ تابع دریافت پیشنهادات برای یک درخواست
  const fetchBidsForRequest = async (requestId, token) => {
    try {
      const response = await fetch(
        config.getApiUrl(`/marketplace/bids/?installation_request=${requestId}`),
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        return data.results || data || [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching bids for request', requestId, error);
      return [];
    }
  };

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const accessToken = localStorage.getItem('access_token');
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('page_size', pageSize);
      if (statusFilter) params.append('status', statusFilter);
      
      const url = config.getApiUrl(`/installations/requests/?${params.toString()}`);
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      
      if (!response.ok) throw new Error('خطا در دریافت درخواست‌ها');
      
      const data = await response.json();
      const requestsList = data.results || [];
      
      // ✅ دریافت پیشنهادات برای هر درخواست
      const requestsWithBids = await Promise.all(
        requestsList.map(async (request) => {
          // دریافت پیشنهادات از API
          const bids = await fetchBidsForRequest(request.id, accessToken);
          
          // پیدا کردن پیشنهاد کاربر جاری
          let userBid = null;
          let hasUserBid = false;
          let bidStatus = null;
          
          if (isInstaller && bids && bids.length > 0) {
            // بررسی با user.id
            userBid = bids.find(bid => {
              // اگر bid.installer.user.id با user.id برابر باشه
              if (bid.installer?.user?.id === user.id) return true;
              // اگر bid.installer.id با installer_profile.id برابر باشه
              if (bid.installer?.id === user?.installer_profile?.id) return true;
              return false;
            });
            hasUserBid = !!userBid;
            bidStatus = userBid?.status || null;
          }
          
          const totalBids = bids?.length || 0;
          let bestBid = null;
          if (bids && bids.length > 0) {
            const sorted = [...bids].sort((a, b) => a.proposed_cost - b.proposed_cost);
            bestBid = sorted[0];
          }
          
          return { 
            ...request, 
            bids: bids,
            has_user_bid: hasUserBid,
            user_bid: userBid,
            bid_status: bidStatus,
            total_bids: totalBids,
            best_bid: bestBid
          };
        })
      );
      
      setRequests(requestsWithBids);
      setTotalCount(data.count || 0);
      setTotalPages(Math.ceil((data.count || 0) / pageSize));
      
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { text: 'در انتظار پیشنهاد', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      'bidding': { text: 'در حال دریافت پیشنهاد', color: 'bg-blue-100 text-blue-800', icon: Users },
      'quoted': { text: 'پیشنهاد داده شده', color: 'bg-purple-100 text-purple-800', icon: DollarSign },
      'accepted': { text: 'تایید شده', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'scheduled': { text: 'زمان‌بندی شده', color: 'bg-indigo-100 text-indigo-800', icon: Calendar },
      'in_progress': { text: 'در حال انجام', color: 'bg-orange-100 text-orange-800', icon: Zap },
      'completed': { text: 'تکمیل شده', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'cancelled': { text: 'لغو شده', color: 'bg-red-100 text-red-800', icon: XCircle },
    };
    const info = statusMap[status] || statusMap.pending;
    const Icon = info.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${info.color}`}>
        <Icon className="w-3 h-3" />
        {info.text}
      </span>
    );
  };

  const getBidStatusBadge = (status) => {
    const statusMap = {
      'pending': { text: 'در انتظار بررسی', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      'accepted': { text: 'پذیرفته شده 🎉', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'rejected': { text: 'رد شده', color: 'bg-red-100 text-red-800', icon: XCircle },
      'expired': { text: 'منقضی شده', color: 'bg-gray-100 text-gray-600', icon: XCircle },
    };
    const info = statusMap[status] || statusMap.pending;
    const Icon = info.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${info.color}`}>
        <Icon className="w-3 h-3" />
        {info.text}
      </span>
    );
  };

  const getCustomerName = (request) => {
    if (request.customer?.full_name) return request.customer.full_name;
    if (request.customer?.username) return request.customer.username;
    if (request.customer?.phone) return request.customer.phone;
    return 'مشتری';
  };

  const getCustomerPhone = (request) => {
    if (request.customer?.phone) return request.customer.phone;
    return '-';
  };

  const openBidModal = (request) => {
    setSelectedRequest(request);
    setBidAmount('');
    setBidDuration('');
    setBidDescription('');
    setBidError('');
    setShowBidModal(true);
  };

  const submitBid = async () => {
    if (!bidAmount || parseInt(bidAmount) <= 0) {
      setBidError('لطفاً مبلغ پیشنهادی را وارد کنید');
      return;
    }
    if (!bidDuration || parseInt(bidDuration) <= 0) {
      setBidError('لطفاً مدت زمان اجرا را وارد کنید');
      return;
    }
    
    setSubmitting(true);
    setBidError('');
    
    try {
      const accessToken = localStorage.getItem('access_token');
      const response = await fetch(config.getApiUrl(`/installations/requests/${selectedRequest.id}/submit_bid/`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          proposed_cost: parseInt(bidAmount),
          proposed_duration: parseInt(bidDuration),
          description: bidDescription
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        alert('✅ پیشنهاد شما با موفقیت ارسال شد');
        setShowBidModal(false);
        fetchRequests();
      } else {
        setBidError(data.error || 'خطا در ارسال پیشنهاد');
      }
    } catch (error) {
      console.error('Error submitting bid:', error);
      setBidError('خطا در ارتباط با سرور');
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return '۰';
    return parseInt(price).toLocaleString('fa-IR');
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const isBiddable = (request) => {
    const isRequestOpen = request.status === 'pending' || request.status === 'bidding';
    const isInstallerUser = user?.role === 'installer';
    const hasNotBid = !request.has_user_bid;
    return isRequestOpen && isInstallerUser && hasNotBid;
  };

  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const getFilteredRequests = () => {
    let filtered = requests;
    
    if (searchTerm) {
      filtered = filtered.filter(request => 
        getCustomerName(request).includes(searchTerm) ||
        request.id?.toString().includes(searchTerm) ||
        request.address?.includes(searchTerm) ||
        getCustomerPhone(request).includes(searchTerm)
      );
    }
    
    if (activeTab === 'my_bids' && isInstaller) {
      filtered = filtered.filter(request => request.has_user_bid);
    } else if (activeTab === 'pending_bids' && isInstaller) {
      filtered = filtered.filter(request => 
        request.has_user_bid && request.bid_status === 'pending'
      );
    }
    
    return filtered;
  };

  // ✅ تابع renderBidAction
  const renderBidAction = (request) => {
    // برای مشتری
    if (!isInstaller) {
      if (request.total_bids > 0) {
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-600 rounded-lg text-xs font-medium">
            <Users className="w-3 h-3" />
            {request.total_bids} پیشنهاد
          </span>
        );
      }
      return null;
    }

    // ========== وضعیت‌های نصاب ==========
    
    // 1. اگر قبلاً پیشنهاد داده
    if (request.has_user_bid) {
      const bid = request.user_bid;
      const status = bid?.status;
      
      if (status === 'accepted') {
        return (
          <div className="flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-700 rounded-lg text-xs font-medium">
              <CheckCircle className="w-3 h-3" />
              پذیرفته شده 🎉
            </span>
            {request.contract_id && (
              <Link
                to={`/contract/${request.contract_id}`}
                className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition"
              >
                <FileText className="w-3 h-3" />
                قرارداد
              </Link>
            )}
          </div>
        );
      }
      
      if (status === 'pending') {
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-yellow-100 text-yellow-700 rounded-lg text-xs font-medium">
            <Clock className="w-3 h-3" />
            در انتظار بررسی
          </span>
        );
      }
      
      if (status === 'rejected') {
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-100 text-red-700 rounded-lg text-xs font-medium">
            <XCircle className="w-3 h-3" />
            رد شده
          </span>
        );
      }
      
      if (status === 'expired') {
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium">
            <Clock className="w-3 h-3" />
            منقضی شده
          </span>
        );
      }
      
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium">
          <Clock className="w-3 h-3" />
          {status || 'در انتظار'}
        </span>
      );
    }

    // 2. درخواست قابل پیشنهاد
    const isRequestOpen = request.status === 'pending' || request.status === 'bidding';
    
    if (isRequestOpen) {
      return (
        <button
          onClick={() => openBidModal(request)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 transition shadow-sm hover:shadow"
        >
          <Send className="w-3.5 h-3.5" />
          پیشنهاد
        </button>
      );
    }
    
    // 3. غیرقابل پیشنهاد
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 text-gray-500 rounded-lg text-xs font-medium">
        <Lock className="w-3 h-3" />
        غیرقابل پیشنهاد
      </span>
    );
  };

  const filteredRequests = getFilteredRequests();

  if (loading && requests.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <>
      <PageSEO title="درخواست‌های نصب | سان ایران" noindex={true} />
      
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          {/* هدر */}
          <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Home className="w-6 h-6 text-blue-600" />
                {isInstaller ? 'درخواست‌های نصب' : 'درخواست‌های من'}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {isInstaller 
                  ? 'مدیریت درخواست‌های نصب و پیشنهادات شما' 
                  : 'مدیریت درخواست‌های نصب سیستم‌های خورشیدی'}
              </p>
            </div>
            <div className="flex gap-2">
              {isCustomer && (
                <Link
                  to="/installation/request"
                  className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                >
                  <Plus className="w-4 h-4" />
                  درخواست جدید
                </Link>
              )}
              <button
                onClick={() => { setRefreshing(true); fetchRequests(); }}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 transition"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                بروزرسانی
              </button>
            </div>
          </div>

          {/* فیلترها و تب‌ها */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 relative">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="جستجو در درخواست‌ها..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pr-10 pl-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">همه وضعیت‌ها</option>
                <option value="pending">در انتظار پیشنهاد</option>
                <option value="bidding">در حال دریافت پیشنهاد</option>
                <option value="quoted">پیشنهاد داده شده</option>
                <option value="accepted">تایید شده</option>
                <option value="scheduled">زمان‌بندی شده</option>
                <option value="in_progress">در حال انجام</option>
                <option value="completed">تکمیل شده</option>
                <option value="cancelled">لغو شده</option>
              </select>
            </div>

            {/* تب‌های نصاب */}
            {isInstaller && (
              <div className="flex gap-2 mt-4 border-t border-gray-100 pt-4">
                <button
                  onClick={() => setActiveTab('all')}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium transition ${
                    activeTab === 'all' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  همه ({requests.length})
                </button>
                <button
                  onClick={() => setActiveTab('my_bids')}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium transition ${
                    activeTab === 'my_bids' 
                      ? 'bg-green-600 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  پیشنهادات من ({requests.filter(r => r.has_user_bid).length})
                </button>
                <button
                  onClick={() => setActiveTab('pending_bids')}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium transition ${
                    activeTab === 'pending_bids' 
                      ? 'bg-yellow-600 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  در انتظار بررسی ({requests.filter(r => r.has_user_bid && r.bid_status === 'pending').length})
                </button>
              </div>
            )}
          </div>

          {/* جدول */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">شناسه</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">مشتری</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">تماس</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">آدرس</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">مساحت</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">پیشنهادات</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">وضعیت</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">تاریخ</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">عملیات</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRequests.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="px-4 py-12 text-center text-gray-500">
                        <Home className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                        <p>هیچ درخواستی یافت نشد</p>
                      </td>
                    </tr>
                  ) : (
                    filteredRequests.map((request) => (
                      <>
                        <tr key={request.id} className="hover:bg-gray-50 transition">
                          <td className="px-4 py-3 text-sm font-mono text-gray-500">
                            #{request.id?.toString().slice(-6)}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {getCustomerName(request)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {getCustomerPhone(request)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                            {request.address}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {request.roof_area} m²
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {request.total_bids > 0 ? (
                              <span className="inline-flex items-center gap-1 text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                                <Users className="w-3 h-3" />
                                {request.total_bids}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {getStatusBadge(request.status)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {new Date(request.created_at).toLocaleDateString('fa-IR')}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Link
                                to={`/installations/${request.id}`}
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                                title="مشاهده جزئیات"
                              >
                                <Eye className="w-4 h-4" />
                              </Link>
                              
                              {renderBidAction(request)}
                              
                              <button
                                onClick={() => toggleRow(request.id)}
                                className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg transition"
                              >
                                {expandedRow === request.id ? (
                                  <ChevronUp className="w-4 h-4" />
                                ) : (
                                  <ChevronDown className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                        
                        {/* ردیف توسعه‌یافته */}
                        {expandedRow === request.id && (
                          <tr>
                            <td colSpan="9" className="px-4 py-4 bg-gray-50 border-t border-gray-200">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-white rounded-lg p-4 border">
                                  <h4 className="text-sm font-bold text-gray-700 mb-2">📋 اطلاعات درخواست</h4>
                                  <div className="space-y-1 text-sm">
                                    <p><span className="text-gray-500">نوع سقف:</span> {request.roof_type || '-'}</p>
                                    <p><span className="text-gray-500">قبض برق:</span> {formatPrice(request.monthly_bill)} تومان</p>
                                    <p><span className="text-gray-500">تاریخ ترجیحی:</span> {new Date(request.preferred_date).toLocaleDateString('fa-IR')}</p>
                                    {request.description && (
                                      <p><span className="text-gray-500">توضیحات:</span> {request.description}</p>
                                    )}
                                  </div>
                                </div>
                                
                                {isInstaller && request.user_bid && (
                                  <div className={`bg-white rounded-lg p-4 border ${
                                    request.bid_status === 'accepted' ? 'border-green-300 bg-green-50' :
                                    request.bid_status === 'rejected' ? 'border-red-300 bg-red-50' :
                                    'border-blue-300 bg-blue-50'
                                  }`}>
                                    <h4 className="text-sm font-bold text-gray-700 mb-2">💵 پیشنهاد شما</h4>
                                    <div className="space-y-1 text-sm">
                                      <p><span className="font-bold text-lg text-gray-900">{formatPrice(request.user_bid.proposed_cost)}</span> تومان</p>
                                      <p><span className="text-gray-500">مدت زمان:</span> {request.user_bid.proposed_duration} روز</p>
                                      {request.user_bid.description && (
                                        <p><span className="text-gray-500">یادداشت:</span> {request.user_bid.description}</p>
                                      )}
                                      <p><span className="text-gray-500">وضعیت:</span> {getBidStatusBadge(request.bid_status)}</p>
                                      <p><span className="text-gray-500">تاریخ ارسال:</span> {new Date(request.user_bid.created_at).toLocaleString('fa-IR')}</p>
                                      {request.bid_status === 'accepted' && request.contract_id && (
                                        <Link
                                          to={`/contract/${request.contract_id}`}
                                          className="inline-flex items-center gap-1 mt-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition"
                                        >
                                          <FileText className="w-4 h-4" />
                                          مشاهده قرارداد
                                        </Link>
                                      )}
                                    </div>
                                  </div>
                                )}
                                
                                {isCustomer && request.best_bid && (
                                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                                    <h4 className="text-sm font-bold text-green-700 mb-2">🌟 بهترین پیشنهاد</h4>
                                    <div className="space-y-1 text-sm">
                                      <p><span className="font-bold text-lg text-green-700">{formatPrice(request.best_bid.proposed_cost)}</span> تومان</p>
                                      <p><span className="text-gray-500">توسط:</span> {request.best_bid.installer?.company_name || 'نصاب'}</p>
                                      <p><span className="text-gray-500">مدت زمان:</span> {request.best_bid.proposed_duration} روز</p>
                                      {request.best_bid.description && (
                                        <p><span className="text-gray-500">یادداشت:</span> {request.best_bid.description}</p>
                                      )}
                                    </div>
                                  </div>
                                )}
                                
                                {!request.has_user_bid && !request.best_bid && (
                                  <div className="bg-gray-50 rounded-lg p-4 border border-dashed text-center text-gray-400">
                                    <p className="text-sm">هیچ پیشنهادی برای این درخواست ثبت نشده است</p>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* صفحه‌بندی */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition"
              >
                قبلی
              </button>
              <span className="px-4 py-2 text-gray-600">صفحه {currentPage} از {totalPages}</span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition"
              >
                بعدی
              </button>
            </div>
          )}
          
          <div className="mt-4 text-center text-sm text-gray-400">
            نمایش {filteredRequests.length} از {totalCount} درخواست
          </div>
        </div>
      </div>

      {/* مودال ارسال پیشنهاد */}
      {showBidModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b border-gray-100 rounded-t-2xl">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">ارسال پیشنهاد قیمت</h2>
                  <p className="text-sm text-gray-500">درخواست #{selectedRequest.id?.toString().slice(-6)}</p>
                </div>
                <button
                  onClick={() => setShowBidModal(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition"
                >
                  <XCircle className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-xl p-4 text-sm">
                <p className="text-gray-500">آدرس:</p>
                <p className="font-medium mt-0.5">{selectedRequest.address}</p>
                <div className="flex gap-4 mt-2 text-gray-500">
                  <span>مساحت: {selectedRequest.roof_area} m²</span>
                  <span>قبض برق: {formatPrice(selectedRequest.monthly_bill)} تومان</span>
                </div>
              </div>

              {bidError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {bidError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  مبلغ پیشنهادی (تومان) *
                </label>
                <input
                  type="number"
                  value={bidAmount}
                  onChange={(e) => setBidAmount(e.target.value)}
                  className="w-full border rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-green-500"
                  placeholder="مثال: 25000000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  مدت زمان اجرا (روز) *
                </label>
                <input
                  type="number"
                  value={bidDuration}
                  onChange={(e) => setBidDuration(e.target.value)}
                  className="w-full border rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-green-500"
                  placeholder="مثال: 14"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  یادداشت (اختیاری)
                </label>
                <textarea
                  value={bidDescription}
                  onChange={(e) => setBidDescription(e.target.value)}
                  rows="3"
                  className="w-full border rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-green-500 resize-none"
                  placeholder="توضیحات تکمیلی..."
                />
              </div>

              <div className="bg-yellow-50 rounded-xl p-3 text-sm text-yellow-800">
                <p className="font-medium">📌 نکته:</p>
                <p>پس از ارسال، مشتری می‌تواند پیشنهاد شما را بررسی کند.</p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowBidModal(false)}
                  className="flex-1 px-4 py-2.5 border rounded-xl hover:bg-gray-50 transition font-medium"
                >
                  انصراف
                </button>
                <button
                  onClick={submitBid}
                  disabled={submitting}
                  className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {submitting ? 'در حال ارسال...' : 'ارسال پیشنهاد'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};